<template>
	<v-container>
		<!-- RESULT VIEW -->
		<div v-if="submitted && resultData">
			<div class="download-wrapper">
				<div class="download-dropdown-wrapper" v-click-outside="closeDropdown">
					<button class="download-btn" @click="toggleDropdown">
						<v-icon x-small left>fas fa-download</v-icon>
						Download VSME Document
						<v-icon x-small right style="margin-left:6px;">
							{{ showDownloadDropdown ? 'fas fa-chevron-up' : 'fas fa-chevron-down' }}
						</v-icon>
					</button>
					<div v-if="showDownloadDropdown" class="download-dropdown-menu">
						<button class="dropdown-item" @click="handleDownloadPDF">
							<v-icon small left class="icon-pdf">fas fa-file-pdf</v-icon>
							As a PDF
						</button>
						<button class="dropdown-item" @click="handleDownloadJSON">
							<v-icon small left class="icon-json">fas fa-file-code</v-icon>
							As a JSON
						</button>
					</div>
				</div>
			</div>
			<v-card class="pa-6 mb-6" outlined>

				<div class="header-bar">
					<div>
						<h2>Water Usage</h2>
					</div>

					<div style="display:flex; gap:8px; align-items:center;">
						<button class="btn-outline" @click="enableEdit">
							<v-icon x-small left>fas fa-pencil</v-icon>
							Edit
						</button>

						<span v-if="isDraft" class="draft-badge">
							<v-icon x-small style="margin-right:4px;">fas fa-clock</v-icon>
							Draft
						</span>

						<span v-else class="submitted-badge">
							<svg width="12" height="12" viewBox="0 0 12 12" fill="none" style="flex-shrink:0">
								<circle cx="6" cy="6" r="5.5" stroke="currentColor" stroke-width="1"/>
								<path d="M3.5 6L5.2 7.7L8.5 4.3" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
							</svg>
							Submitted
						</span>
					</div>
				</div>

				<!-- 1. Water Consumption -->
				<div class="section">
					<div class="section-title">1 · Water Consumption</div>

					<div class="metric-grid">
						<div class="metric-card">
							<p class="metric-label">Total annual water consumption</p>
							<p class="metric-value">{{ fmt(resultData.totalConsumption, ' m³') }}</p>
						</div>

						<div class="metric-card">
							<p class="metric-label">Monitors water usage?</p>
							<p class="metric-value" v-html="badge(resultData.monitorsUsage)"></p>
						</div>

						<div class="metric-card" v-if="resultData.monitorsUsage === 'Yes'">
							<p class="metric-label">Monitoring frequency</p>
							<p class="metric-value">{{ fmt(resultData.monitoringFrequency) }}</p>
						</div>
					</div>
				</div>

				<!-- 2. Water Sources -->
				<div class="section">
					<div class="section-title">2 · Water Sources</div>

					<div
						v-if="allWaterSources.length"
						class="tag-list"
					>
						<span
							v-for="source in allWaterSources"
							:key="source"
							class="tag"
						>
							{{ source }}
						</span>
					</div>

					<span v-else class="empty-note">None reported</span>
				</div>

				<!-- 3. Wastewater Management -->
				<div class="section">
					<div class="section-title">3 · Wastewater Management</div>

					<div class="row-grid">
						<div class="row-item">
							<span class="row-key">Treats wastewater before discharge?</span>
							<span class="row-val" v-html="badge(resultData.treatsWastewater)"></span>
						</div>

						<div class="row-item">
							<span class="row-key">Recycles or reuses water?</span>
							<span class="row-val" v-html="badge(resultData.recyclsWater)"></span>
						</div>
					</div>
				</div>

				<!-- 4. Efficiency & Management -->
				<div class="section">
					<div class="section-title">4 · Efficiency & Management</div>

					<div class="row-grid" style="margin-bottom:1rem;">
						<div class="row-item">
							<span class="row-key">Water reduction initiatives?</span>
							<span class="row-val" v-html="badge(resultData.hasEfficiencyInitiatives)"></span>
						</div>

						<div class="row-item">
							<span class="row-key">Experienced water-related disruptions?</span>
							<span class="row-val" v-html="badge(resultData.hasDisruptions)"></span>
						</div>

						<div class="row-item">
							<span class="row-key">Tracks water usage per unit of output?</span>
							<span class="row-val" v-html="badge(resultData.tracksIntensity)"></span>
						</div>
					</div>

					<div
						v-if="resultData.hasEfficiencyInitiatives === 'Yes' && resultData.efficiencyDescription"
						class="text-block"
					>
						<div class="row-key" style="margin-bottom:4px;">Efficiency initiative description</div>
						<div class="text-body">{{ resultData.efficiencyDescription }}</div>
					</div>
				</div>

			</v-card>
		</div>

		<!-- FORM VIEW -->
		<v-form v-else ref="form" v-model="formIsValid">
			<v-card class="pa-3 mb-6" outlined>
				<v-alert
					v-if="isDraft"
					type="warning"
					dense
					outlined
					class="mb-3"
					icon="fas fa-clock"
				>
					You have a saved draft. Complete and submit when ready.
				</v-alert>
				<v-col cols="12" class="d-flex justify-center"><h2>WATER USAGE</h2></v-col>

				<!-- 1. Water Consumption -->
				<v-row class="contentRow">
					<v-col cols="12"><h2>1. Water Consumption</h2></v-col>

					<v-col cols="12">
						<v-text-field
							v-model="form.totalConsumption"
							label="Q1. Total annual water consumption (m³)"
							type="number"
							filled
							dense
						/>
					</v-col>
					<v-col cols="12" class="desc-wrapper">
						<v-btn text small color="primary" @click="toggleDesc('q1')">
							{{ showDesc.q1 ? 'Hide description' : 'Show description' }}
							<v-icon x-small class="icon-circle ml-2">fas fa-section</v-icon>
						</v-btn>
						<div v-if="showDesc.q1" class="grey--text text--darken-1 mt-1 text-caption">
							(ESRS E3-4, paragraph 34: The undertaking shall disclose total water consumption and withdrawal volumes, including quantitative metrics where relevant.)
						</div>
					</v-col>

					<v-col cols="12">
						<v-select
							v-model="form.monitorsUsage"
							:items="['Yes', 'No']"
							label="Q2. Do you monitor water usage regularly?"
							filled
							dense
						/>
					</v-col>
					<v-col cols="12" md="6" v-if="form.monitorsUsage === 'Yes'">
						<v-text-field
							v-model="form.monitoringFrequency"
							label="Monitoring frequency (e.g. Monthly, Quarterly)"
							filled
							dense
						/>
					</v-col>
					<v-col cols="12" class="desc-wrapper">
						<v-btn text small color="primary" @click="toggleDesc('q2')">
							{{ showDesc.q2 ? 'Hide description' : 'Show description' }}
							<v-icon x-small class="icon-circle ml-2">fas fa-section</v-icon>
						</v-btn>
						<div v-if="showDesc.q2" class="grey--text text--darken-1 mt-1 text-caption">
							(ESRS E3-5, paragraph 38: The undertaking shall disclose whether and how it monitors water consumption across its operations.)
						</div>
					</v-col>
				</v-row>

				<!-- 2. Water Sources -->
				<v-row class="contentRow">
					<v-col cols="12"><h2>2. Water Sources</h2></v-col>

					<v-col cols="12">
						<h3>Q3. What are your main water sources? (Select all that apply)</h3>
						<v-checkbox v-model="form.waterSources" label="Municipal supply" value="Municipal supply" hide-details />
						<v-checkbox v-model="form.waterSources" label="Groundwater"      value="Groundwater"     hide-details />
						<v-checkbox v-model="form.waterSources" label="Surface water"    value="Surface water"   hide-details />
						<v-checkbox v-model="form.waterSourcesHasOther" label="Other" hide-details />
						<v-text-field
							v-if="form.waterSourcesHasOther"
							v-model="form.waterSourcesOther"
							label="Please specify"
							filled
							dense
							class="mt-2"
						/>
					</v-col>
					<v-col cols="12" class="desc-wrapper mt-2">
						<v-btn text small color="primary" @click="toggleDesc('q3')">
							{{ showDesc.q3 ? 'Hide description' : 'Show description' }}
							<v-icon x-small class="icon-circle ml-2">fas fa-section</v-icon>
						</v-btn>
						<div v-if="showDesc.q3" class="grey--text text--darken-1 mt-1 text-caption">
							(ESRS E3-4, paragraph 35: The undertaking shall disclose the sources of water used, including municipal, groundwater, and surface water sources.)
						</div>
					</v-col>
				</v-row>

				<!-- 3. Wastewater Management -->
				<v-row class="contentRow">
					<v-col cols="12"><h2>3. Wastewater Management</h2></v-col>

					<v-col cols="12">
						<v-select
							v-model="form.treatsWastewater"
							:items="['Yes', 'No', 'Partially']"
							label="Q4. Do you treat wastewater before discharge?"
							filled
							dense
						/>
					</v-col>
					<v-col cols="12" class="desc-wrapper">
						<v-btn text small color="primary" @click="toggleDesc('q4')">
							{{ showDesc.q4 ? 'Hide description' : 'Show description' }}
							<v-icon x-small class="icon-circle ml-2">fas fa-section</v-icon>
						</v-btn>
						<div v-if="showDesc.q4" class="grey--text text--darken-1 mt-1 text-caption">
							(ESRS E3-6, paragraph 40: The undertaking shall disclose how wastewater is treated before discharge, including treatment methods and compliance measures.)
						</div>
					</v-col>

					<v-col cols="12">
						<v-select
							v-model="form.recyclsWater"
							:items="['Yes', 'No', 'Partially']"
							label="Q5. Do you recycle or reuse water in your operations?"
							filled
							dense
						/>
					</v-col>
					<v-col cols="12" class="desc-wrapper">
						<v-btn text small color="primary" @click="toggleDesc('q5')">
							{{ showDesc.q5 ? 'Hide description' : 'Show description' }}
							<v-icon x-small class="icon-circle ml-2">fas fa-section</v-icon>
						</v-btn>
						<div v-if="showDesc.q5" class="grey--text text--darken-1 mt-1 text-caption">
							(ESRS E3-7, paragraph 42: The undertaking shall disclose water recycling and reuse practices as part of circular water management.)
						</div>
					</v-col>
				</v-row>

				<!-- 4. Efficiency & Management -->
				<v-row class="contentRow">
					<v-col cols="12"><h2>4. Efficiency & Management</h2></v-col>

					<v-col cols="12">
						<v-select
							v-model="form.hasEfficiencyInitiatives"
							:items="['Yes', 'No']"
							label="Q6. Do you have water reduction or efficiency initiatives?"
							filled
							dense
						/>
					</v-col>
					<v-col cols="12" v-if="form.hasEfficiencyInitiatives === 'Yes'">
						<v-textarea
							v-model="form.efficiencyDescription"
							label="Describe briefly"
							filled
							dense
							rows="3"
						/>
					</v-col>
					<v-col cols="12" class="desc-wrapper">
						<v-btn text small color="primary" @click="toggleDesc('q6')">
							{{ showDesc.q6 ? 'Hide description' : 'Show description' }}
							<v-icon x-small class="icon-circle ml-2">fas fa-section</v-icon>
						</v-btn>
						<div v-if="showDesc.q6" class="grey--text text--darken-1 mt-1 text-caption">
							(ESRS E3-8, paragraph 44: The undertaking shall describe water efficiency initiatives and actions aimed at reducing water consumption.)
						</div>
					</v-col>

					<v-col cols="12">
						<v-select
							v-model="form.hasDisruptions"
							:items="['Yes', 'No']"
							label="Q7. Have you experienced water-related disruptions?"
							filled
							dense
						/>
					</v-col>
					<v-col cols="12" class="desc-wrapper">
						<v-btn text small color="primary" @click="toggleDesc('q7')">
							{{ showDesc.q7 ? 'Hide description' : 'Show description' }}
							<v-icon x-small class="icon-circle ml-2">fas fa-section</v-icon>
						</v-btn>
						<div v-if="showDesc.q7" class="grey--text text--darken-1 mt-1 text-caption">
							(ESRS E3-9, paragraph 45: The undertaking shall disclose exposure to water-related risks, including operational disruptions due to water scarcity or availability.)
						</div>
					</v-col>

					<v-col cols="12">
						<v-select
							v-model="form.tracksIntensity"
							:items="['Yes', 'No']"
							label="Q8. Do you track water usage per product or unit of output?"
							filled
							dense
						/>
					</v-col>
					<v-col cols="12" class="desc-wrapper">
						<v-btn text small color="primary" @click="toggleDesc('q8')">
							{{ showDesc.q8 ? 'Hide description' : 'Show description' }}
							<v-icon x-small class="icon-circle ml-2">fas fa-section</v-icon>
						</v-btn>
						<div v-if="showDesc.q8" class="grey--text text--darken-1 mt-1 text-caption">
							(ESRS E3-5, paragraph 38: The undertaking shall disclose water intensity metrics linking consumption to production output where relevant.)
						</div>
					</v-col>
				</v-row>

				<!-- Error -->
				<v-row class="contentRow" v-if="errorMessage">
					<v-col cols="12">
						<v-alert type="error" dense>{{ errorMessage }}</v-alert>
					</v-col>
				</v-row>

				<!-- Actions -->
				<v-row class="contentRow">
					<v-col cols="12" class="d-flex justify-center">
						<v-btn outlined color="primary" class="mr-5" :loading="loading" :disabled="loading" @click="saveDraft">
							<v-icon x-small left>fas fa-floppy-disk</v-icon>
							Save as Draft
						</v-btn>
						<v-btn color="primary" :loading="loading" :disabled="loading" @click="submit">
							Submit
						</v-btn>
					</v-col>
				</v-row>
			</v-card>
		</v-form>
	</v-container>
</template>

<script>
import CsrAPI from "../api/csr/csr"

export default {
	props: {
		initialData: { type: Object, default: null },
		isEditMode:  { type: Boolean, default: false },
	},

	data() {
		return {
			formIsValid: false,
			loading: false,
			errorMessage: "",
			showDesc: {},
			form: {
				totalConsumption: null,
				monitorsUsage: null,
				monitoringFrequency: "",
				waterSources: [],
				waterSourcesHasOther: false,
				waterSourcesOther: "",
				treatsWastewater: null,
				recyclsWater: null,
				hasEfficiencyInitiatives: null,
				efficiencyDescription: "",
				hasDisruptions: null,
				tracksIntensity: null,
				isDraft: false,
			},
			submitted: false,
			resultData: null,
			isDraft: false,
			hasExistingRecord: false,
			showDownloadDropdown: false,
		}
	},

	watch: {
		initialData: {
			immediate: true,
			handler(res) {
				if (!res) return
				this.mapDataToForm(res)
			},
		},
	},

	computed: {
		allWaterSources() {
			if (!this.resultData) return []
			const list = [...(this.resultData.waterSources || [])]
			if (this.resultData.waterSourcesOther) {
				list.push(this.resultData.waterSourcesOther)
			}
			return list
		},
	},

	async created() {
		this.loadData()
	},

	methods: {
		toggleDesc(key) {
			this.$set(this.showDesc, key, !this.showDesc[key])
		},

		enableEdit() {
			this.submitted = false
		},

		async loadData() {
			try {
				const res = await CsrAPI.getVsmeWater()

				if (!res || res.Total_Consumption === undefined || res.Total_Consumption === null) {
					this.submitted = false
					return
				}

				this.hasExistingRecord = true

				this.mapDataToForm(res)
				this.resultData = { ...this.form }

				if (res.Is_Draft === true || res.Is_Draft === 1) {
					// Draft exists — reopen form pre-filled, don't show result view
					this.isDraft = true
					this.submitted = false
				} else if (!this.isEditMode) {
					this.submitted = true
				}

			} catch (error) {
				console.error("Failed to load water usage data:", error)
			}
		},

		mapDataToForm(res) {
			const safeParse = (val) => {
				try {
					return JSON.parse(val || "[]")
				} catch {
					return []
				}
			}

			const waterSources = safeParse(res.Water_Sources)

			this.form = {
				...this.form,
				totalConsumption:         res.Total_Consumption          ?? null,
				monitorsUsage:            res.Monitors_Usage             || null,
				monitoringFrequency:      res.Monitoring_Frequency       || "",
				waterSources:             Array.isArray(waterSources) ? waterSources : [],
				waterSourcesHasOther:     !!res.Water_Sources_Other,
				waterSourcesOther:        res.Water_Sources_Other        || "",
				treatsWastewater:         res.Treats_Wastewater          || null,
				recyclsWater:             res.Recycls_Water              || null,
				hasEfficiencyInitiatives: res.Has_Efficiency_Initiatives || null,
				efficiencyDescription:    res.Efficiency_Description     || "",
				hasDisruptions:           res.Has_Disruptions            || null,
				tracksIntensity:          res.Tracks_Intensity           || null,
			}
		},

		async _saveOrUpdate(isDraft) {
			const f = this.form
			const args = [
				f.totalConsumption,
				f.monitorsUsage,
				f.monitoringFrequency,
				f.waterSources,
				f.waterSourcesOther,
				f.treatsWastewater,
				f.recyclsWater,
				f.hasEfficiencyInitiatives,
				f.efficiencyDescription,
				f.hasDisruptions,
				f.tracksIntensity,
				isDraft,
			]
			if (this.hasExistingRecord) {
				await CsrAPI.updateVsmeWater(...args)
			} else {
				await CsrAPI.saveVsmeWater(...args)
				this.hasExistingRecord = true   // flip after first insert
			}
		},

		async submit() {
			this.loading = true
			this.errorMessage = ""
			try {
				await this._saveOrUpdate(false)
				this.resultData = { ...this.form }
				this.isDraft = false
				this.submitted = true
				this.$emit("saved")
			} catch (error) {
				this.errorMessage = error.message
			} finally {
				this.loading = false
			}
		},

		async saveDraft() {
			this.loading = true
			this.errorMessage = ""
			try {
				await this._saveOrUpdate(true)
				this.isDraft = true
				this.$emit("drafted")   
			} catch (error) {
				this.errorMessage = error.message
			} finally {
				this.loading = false
			}
		},

		toggleDropdown() {
			this.showDownloadDropdown = !this.showDownloadDropdown
		},

		closeDropdown() {
			this.showDownloadDropdown = false
		},

		handleDownloadPDF() {
			this.showDownloadDropdown = false
			CsrAPI.downloadVsmePDF()
		},
		
		handleDownloadJSON() {
			this.showDownloadDropdown = false
			CsrAPI.downloadJSON()
		},

		fmt(val, suffix = "") {
			if (val === null || val === undefined || val === "") {
				return "—"
			}
			return val + suffix
		},

		badge(val) {
			if (val === "Yes") {
				return "<span class='yes-badge'>Yes</span>"
			}
			if (val === "No") {
				return "<span class='no-badge'>No</span>"
			}
			if (val === "Partially") {
				return "<span class='partial-badge'>Partially</span>"
			}
			return val || "—"
		},
	},
}
</script>

<style scoped>
.esgcontainer h1 {
  color: #1e7c83;
  font-weight: bold;
}

h2 {
  color: #1e7c83;
}

h3 {
  color: #424242;
  margin-bottom: 8px;
}

.contentRow {
  margin-bottom: 16px;
}

.icon-circle {
	border: 1px solid currentColor;
	border-radius: 50%;
	padding: 4px;
}

.desc-wrapper {
	padding-top: 0 !important;
	margin-top: -20px;
}

.header-bar {
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 1.25rem;
}

.submitted-badge {
	display: flex;
	align-items: center;
	gap: 5px;
	background: #e8f5e9;
	color: #2e7d32;
	padding: 4px 12px;
	border-radius: 20px;
}

.btn-outline {
	border: 1px solid #bdbdbd;
	background: none;
	border-radius: 6px;
	padding: 5px 12px;
	color: #424242;
	cursor: pointer;
	display: flex;
	align-items: center;
	gap: 4px;
}

.btn-outline:hover {
	background: #f5f5f5;
}

.download-wrapper {
	display: flex;
	justify-content: flex-end;
	margin-bottom: 10px; 
}

.download-btn {
	border: 1px solid #bdbdbd;
	background: none;
	border-radius: 6px;
	padding: 5px 12px;
	color: #424242;
	cursor: pointer;
	display: flex;
	align-items: center;
	gap: 4px;
}

.download-dropdown-wrapper {
    position: relative;
    display: inline-block;
}

.download-dropdown-menu {
    position: absolute;
    right: 0;
    top: calc(100% + 4px);
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0,0,0,0.10);
    min-width: 160px;
    z-index: 100;
    overflow: hidden;
}

.dropdown-item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 10px 16px;
    background: none;
    border: none;
    color: #424242;
    cursor: pointer;
    text-align: left;
    font-size: 0.875rem;
    transition: background 0.15s;
}

.dropdown-item:hover {
    background: #f5f5f5;
}

.icon-pdf {
    color: #e53935 !important;  
}

.icon-json {
    color: #f9a825 !important;
}

.section {
	background: #fff;
	border: 1px solid #2d9b9b;
	border-radius: 10px;
	padding: 1.25rem 1.5rem;
	margin-bottom: 1rem;
}

.section-title {
	color: #9e9e9e;
	text-transform: uppercase;
	letter-spacing: 0.06em;
	margin: 0 0 1rem;
}

.metric-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(90px, 1fr));
	gap: 10px;
	margin-bottom: 0.5rem;
}

.metric-card {
	background: #f5f5f5;
	border-radius: 8px;
	padding: 0.75rem 1rem;
}

.metric-label {
	color: #757575;
	margin: 0 0 4px;
}

.metric-value {
	color: #212121;
	margin: 0;
}

.row-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
	gap: 12px;
}

.row-item {
	display: flex;
	flex-direction: column;
	gap: 3px;
}

.row-key {
	color: #9e9e9e;
}

.row-val {
	color: #212121;
}

.tag-list {
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
}

.tag {
	background: #e0f2f1;
	color: #00695c;
	padding: 3px 10px;
	border-radius: 20px;
}

.empty-note {
	color: #bdbdbd;
}

.yes-badge {
	background: #e8f5e9;
	color: #2e7d32;
	padding: 2px 9px;
	border-radius: 20px;
	display: inline-block;
}

.no-badge {
	background: #f5f5f5;
	color: #757575;
	padding: 2px 9px;
	border-radius: 20px;
	display: inline-block;
}

.partial-badge {
	background: #fff8e1;
	color: #f57f17;
	padding: 2px 9px;
	border-radius: 20px;
	display: inline-block;
}

.text-block {
	background: #f9f9f9;
	border-radius: 6px;
	padding: 10px 14px;
}

.text-body {
	color: #424242;
	line-height: 1.6;
}

.sub-label {
	color: #9e9e9e;
	margin-bottom: 8px;
}

.divider {
	border: none;
	border-top: 0.5px solid #e0e0e0;
	margin: 1rem 0;
}

.draft-badge {
    display: flex;
    align-items: center;
    gap: 5px;
    background: #fff8e1;
    color: #f57f17;
    padding: 4px 12px;
    border-radius: 20px;
}

@media print {
  .btn-outline { display: none !important; }
}
</style>